/*
 * Design: Gradient Noir – Blog post detail page
 * Hero → Article body → Related posts → CTA
 */
import { Link, useParams, useLocation } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { formatDate, categories } from "@/data/blogPosts";
import { useApiData } from "@/hooks/useApiData";
import type { BlogPost as BlogPostType } from "@shared/types";
import {
  Clock,
  ArrowRight,
  ArrowLeft,
  Calendar,
  User,
  BookOpen,
  Share2,
  Facebook,
  Linkedin,
  Twitter,
} from "lucide-react";
import { useEffect } from "react";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function renderContent(blocks: string[]) {
  return blocks.map((block, i) => {
    // Check if block starts with ## heading
    if (block.startsWith("## ")) {
      const parts = block.split("\n\n");
      const heading = parts[0].replace("## ", "");
      const body = parts.slice(1).join("\n\n");
      return (
        <div key={i} className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">
            {heading}
          </h2>
          {body && (
            <p className="text-white/60 leading-[1.9] text-[17px]">{body}</p>
          )}
        </div>
      );
    }
    // Regular paragraph
    return (
      <p
        key={i}
        className="text-white/60 leading-[1.9] text-[17px] mb-6"
      >
        {block}
      </p>
    );
  });
}

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const { data: post, loading } = useApiData<BlogPostType>(`/blog-posts/${params.slug || ""}`);
  const { data: allPosts } = useApiData<BlogPostType[]>("/blog-posts");

  useEffect(() => {
    if (!loading && !post) {
      navigate("/blog");
    }
  }, [post, loading, navigate]);

  if (loading || !post) return null;

  // Compute related posts client-side
  const related = (allPosts || [])
    .filter((p) => p.slug !== post.slug && p.category === post.category)
    .concat((allPosts || []).filter((p) => p.slug !== post.slug && p.category !== post.category))
    .slice(0, 3);
  const categoryLabel =
    categories.find((c) => c.id === post.category)?.label || post.category;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = post.title;

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-8">
            <Link
              href="/"
              className="hover:text-[#66CDAA] transition-colors"
            >
              דף הבית
            </Link>
            <span>/</span>
            <Link
              href="/blog"
              className="hover:text-[#66CDAA] transition-colors"
            >
              בלוג
            </Link>
            <span>/</span>
            <span className="text-white/60 line-clamp-1">{post.title}</span>
          </div>

          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="px-3 py-1.5 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 text-xs text-[#66CDAA] font-semibold">
                {categoryLabel}
              </span>
              {post.featured && (
                <span className="px-3 py-1.5 rounded-full bg-[#66CDAA] text-[#0F172A] text-xs font-bold">
                  מאמר מומלץ
                </span>
              )}
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-white/40">
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime} דק׳ קריאה
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="section-navy">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto -mt-4">
              <div className="rounded-2xl overflow-hidden border border-white/5 shadow-2xl shadow-black/30">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-64 lg:h-96 object-cover"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 lg:py-20 section-navy">
        <div className="container">
          <div className="grid lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
            {/* Main Content */}
            <article className="lg:col-span-8">
              <AnimatedSection>
                <div className="glass-card p-8 lg:p-10">
                  {/* Excerpt / Lead */}
                  <p className="text-xl text-white/70 leading-relaxed mb-10 pb-8 border-b border-white/5 font-medium">
                    {post.excerpt}
                  </p>

                  {/* Content */}
                  <div className="blog-content">{renderContent(post.content)}</div>

                  {/* Share */}
                  <div className="mt-10 pt-8 border-t border-white/5">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-sm text-white/40">
                        <Share2 className="w-4 h-4" />
                        <span>שתפו את המאמר:</span>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors group"
                          title="שתפו בפייסבוק"
                        >
                          <Facebook className="w-4 h-4 text-white/40 group-hover:text-[#1877F2]" />
                        </a>
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-[#1DA1F2]/20 flex items-center justify-center transition-colors group"
                          title="שתפו בטוויטר"
                        >
                          <Twitter className="w-4 h-4 text-white/40 group-hover:text-[#1DA1F2]" />
                        </a>
                        <a
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-[#0A66C2]/20 flex items-center justify-center transition-colors group"
                          title="שתפו בלינקדאין"
                        >
                          <Linkedin className="w-4 h-4 text-white/40 group-hover:text-[#0A66C2]" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Back to Blog */}
              <AnimatedSection>
                <div className="mt-8">
                  <Link
                    href="/blog"
                    className="inline-flex items-center gap-2 text-white/50 hover:text-[#66CDAA] transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                    חזרה לבלוג
                  </Link>
                </div>
              </AnimatedSection>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                {/* About Author */}
                <AnimatedSection delay={100}>
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-3 font-[Rubik]">
                      על הכותב
                    </h3>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#66CDAA] to-[#3DD5B0] flex items-center justify-center">
                        <User className="w-6 h-6 text-[#0F172A]" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-sm font-[Rubik]">
                          {post.author}
                        </div>
                        <div className="text-xs text-white/40">
                          מומחי שיווק דיגיטלי
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-white/45 leading-relaxed">
                      צוות המומחים של אפסייט משלב ניסיון של 20+ שנה בבניית
                      אתרים, קידום אורגני ושיווק דיגיטלי.
                    </p>
                  </div>
                </AnimatedSection>

                {/* Categories */}
                <AnimatedSection delay={200}>
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4 font-[Rubik]">
                      קטגוריות
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {categories
                        .filter((c) => c.id !== "all")
                        .map((cat) => (
                          <Link
                            key={cat.id}
                            href="/blog"
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              cat.id === post.category
                                ? "bg-[#66CDAA]/15 text-[#66CDAA] border border-[#66CDAA]/25"
                                : "bg-white/5 text-white/40 hover:text-white/60 border border-white/5"
                            }`}
                          >
                            {cat.label}
                          </Link>
                        ))}
                    </div>
                  </div>
                </AnimatedSection>

                {/* Related Posts */}
                {related.length > 0 && (
                  <AnimatedSection delay={300}>
                    <div className="glass-card p-6">
                      <h3 className="text-lg font-bold text-white mb-4 font-[Rubik]">
                        מאמרים קשורים
                      </h3>
                      <div className="space-y-4">
                        {related.map((rp) => (
                          <Link
                            key={rp.slug}
                            href={`/blog/${rp.slug}`}
                            className="block group"
                          >
                            <div className="flex gap-3">
                              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                                <img
                                  src={rp.image}
                                  alt={rp.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  loading="lazy"
                                />
                              </div>
                              <div className="min-w-0">
                                <h4 className="text-sm font-bold text-white/80 group-hover:text-[#66CDAA] transition-colors line-clamp-2 leading-snug font-[Rubik]">
                                  {rp.title}
                                </h4>
                                <span className="text-xs text-white/30 mt-1 block">
                                  {formatDate(rp.date)}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </AnimatedSection>
                )}

                {/* CTA Card */}
                <AnimatedSection delay={400}>
                  <div className="bg-gradient-to-br from-[#66CDAA]/10 to-[#3DD5B0]/5 rounded-2xl p-6 border border-[#66CDAA]/15">
                    <BookOpen className="w-8 h-8 text-[#66CDAA] mb-3" />
                    <h3 className="text-lg font-bold text-white mb-2 font-[Rubik]">
                      צריכים עזרה מקצועית?
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed mb-4">
                      הצוות שלנו ישמח לעזור לכם ליישם את הטיפים מהמאמר הזה.
                    </p>
                    <Link
                      href="/contact"
                      className="glow-btn inline-block text-sm px-5 py-2.5"
                    >
                      דברו איתנו
                    </Link>
                  </div>
                </AnimatedSection>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Posts Section */}
      {related.length > 0 && (
        <section className="py-16 lg:py-24 section-dark relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
          <div className="container">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4 font-[Rubik]">
                  מאמרים <span className="gradient-text">נוספים</span>
                </h2>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {related.map((rp, i) => (
                <AnimatedSection key={rp.slug} delay={i * 100}>
                  <Link
                    href={`/blog/${rp.slug}`}
                    className="block glass-card overflow-hidden group hover:-translate-y-1.5 transition-all duration-300 hover:border-[#66CDAA]/20 h-full"
                  >
                    <div className="h-40 overflow-hidden relative">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-white/40 mb-2">
                        <span>{formatDate(rp.date)}</span>
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        <span>{rp.readTime} דק׳</span>
                      </div>
                      <h3 className="text-base font-bold text-white font-[Rubik] group-hover:text-[#66CDAA] transition-colors line-clamp-2 leading-snug">
                        {rp.title}
                      </h3>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              בואו <span className="gradient-text">נתקדם</span> יחד
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              רוצים ליישם את מה שקראתם? דברו איתנו ונבנה יחד את האסטרטגיה
              הדיגיטלית שלכם.
            </p>
            <Link
              href="/contact"
              className="glow-btn inline-block text-lg px-10 py-4"
            >
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
